import styles from "./VolunteerProfileSkillsInterest.module.scss";
import { useTranslation } from "../../i18n";
import { useContext, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { AuthContext } from "../../context/AuthContext";
import { useEnumStore } from "../../store/enumStore";
import { SkillsInterestsList } from "../SkillsInterestsList/SkillsInterestsList";
import { EventInterestsSkillsSection } from "../EventInterestsSkillsSection/EventInterestsSkillsSection";

interface VolunteerSkillsInterestsData {
  skills: string[];
  interests: string[];
}

export const VolunteerProfileSkillsInterest = () => {
  const { t } = useTranslation();
  const skills = useEnumStore((state) => state.skills);
  const interests = useEnumStore((state) => state.interests);
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error("AuthContext must be used within an AuthProvider");
  }

  const { currentUser, editUser } = authContext;

  const [dialogOpen, setDialogOpen] = useState<"" | "skills" | "interests">("");
  const [volunteerData, setVolunteerData] = useState<VolunteerSkillsInterestsData>({
    skills: ((currentUser?.userData as any)?.skills || []).map((id: number) => String(id)),
    interests: ((currentUser?.userData as any)?.interests || []).map((id: number) => String(id)),
  });

  useEffect(() => {
    if (currentUser) {
      const userData = currentUser.userData as any;
      setVolunteerData({
        skills: (userData?.skills || []).map((id: number) => String(id)),
        interests: (userData?.interests || []).map((id: number) => String(id)),
      });
    }
  }, [currentUser]);

  const handleCheckboxToggle = (value: string, key: "skills" | "interests") => {
    const strValue = String(value);
    const alreadyExists = volunteerData?.[key].includes(strValue);
    if (alreadyExists) {
      setVolunteerData((prevState) => ({
        ...prevState,
        [key]: prevState[key].filter((item) => item !== strValue),
      }));
    } else {
      setVolunteerData((prevState) => ({
        ...prevState,
        [key]: [...prevState[key], strValue],
      }));
    }
  };

  const handleEditConfirm = () => {
    const dataToSend = {
      // skills and interests are already strings, backend expects strings
      skills: volunteerData.skills,
      interests: volunteerData.interests,
    };
    editUser(dataToSend, "volunteer");
    if (dialogOpen !== "") {
      setDialogOpen("");
    }
  };

  const handleClose = () => {
    setDialogOpen("");
    const userData = currentUser?.userData as any;
    setVolunteerData({
      skills: (userData?.skills || []).map((id: number) => String(id)),
      interests: (userData?.interests || []).map((id: number) => String(id)),
    });
  };

  return (
    <>
      <div className={styles.skills}>
        <SkillsInterestsList
          items={volunteerData?.skills.map(id => parseInt(id, 10))}
          type="skills"
          title={t("volunteer.skills")}
          emptyMessage={t("volunteer.noSkillsMine")}
        />
        <Button variant="contained" onClick={() => setDialogOpen("skills")}>
          {t("volunteer.editSkills")}
        </Button>
      </div>
      <div className={styles.interests}>
        <SkillsInterestsList
          items={volunteerData?.interests.map(id => parseInt(id, 10))}
          type="interests"
          title={t("volunteer.interests")}
          emptyMessage={t("volunteer.noInterestsMine")}
        />
        <Button variant="contained" onClick={() => setDialogOpen("interests")}>
          {t("volunteer.editInterests")}
        </Button>
      </div>
      <Dialog
        open={dialogOpen !== ""}
        onClose={() => handleClose()}
        aria-labelledby="profile-dialog-title"
        maxWidth="xl"
        className={styles.dialog}
      >
        <DialogTitle id="profile-dialog-title" className={styles.dialogTitle}>
          {dialogOpen === "interests"
            ? t("volunteer.editInterests")
            : t("volunteer.editSkills")}
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          {dialogOpen === "skills" && (
            <EventInterestsSkillsSection
              title=""
              items={skills as any}
              selectedIds={volunteerData?.skills || []}
              onToggle={(id) => handleCheckboxToggle(id, "skills")}
              editMode={true}
              userType="volunteer"
              showSubItems={true}
              type="skills"
            />
          )}
          {dialogOpen === "interests" && (
            <EventInterestsSkillsSection
              title=""
              items={interests as any}
              selectedIds={volunteerData?.interests || []}
              onToggle={(id) => handleCheckboxToggle(id, "interests")}
              editMode={true}
              userType="volunteer"
              showSubItems={true}
              type="interests"
            />
          )}
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button variant="outlined" onClick={() => handleClose()}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              handleEditConfirm()
            }
          >
            {t("common.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

